import { redirect, RouteObject } from 'react-router-dom';

import { RouterPaths } from './paths';
import { DashboardLayout } from '../views/dashboardLayout';
import ErrorBoundary from '../views/errorBounary/errorBoundary';

import {
  ABFPaymentApps,
  ApplicationTokens,
  BankAccounts,
  BlockchainFee,
  CompanyTypes,
  Countries,
  Details,
  Dictionaries,
  Documents,
  ExchangeFeeGroups,
  ExchangeFees,
  ExchangeRates,
  FeeGroups,
  Fees,
  Invoices,
  Providers,
  ProviderWallet,
  Ledgers,
  NotificationTemplate,
  NotificationTemplates,
  Profile,
  Profiles,
  Reports,
  SumsubInfo,
  Transaction,
  Transactions,
  TransitAddresses,
  Wallets,
} from '../views';
import { MerchantСoinСonfig } from '../views/MerchantСoinСonfig';
import { MerchantCoinConfig } from '../views/Profile/MerchantCoinConfig';
import { MerchantPayments } from '../views/Profile/MerchantPayments';
import { MerchantPaymentDetails } from '../views/Profile/MerchantPayments/MerchantPaymentDetails';
import { Provider, ProviderFees } from '../views/ProviderFees';
import { BlockchainFeeMarkups } from '../views/BlockchainFeeMarkups';
import { TransitWithdrawals } from '../views/TransitAddresses/TransitWithdrawals';
import { permissionsGuard } from '../utils/permission';
import { RoleDetails, Roles, Users, UsersAndRoles } from '../views/UsersAndRoles';
import { FeeGroupDetails } from '../views/Fees/FeeGroupDetails';
import { FeeLevels } from '../views/Fees/FeeLevels';
import { MerchantConfig } from '../views/Profile/MerchantConfig';
import { SandboxUsers } from '../views/Profile/SandboxUsers';
import { Dashboard } from '../views/Dashboard';

export const routes: RouteObject[] = [
  {
    path: RouterPaths.Root,
    element: <DashboardLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, loader: () => redirect(RouterPaths.Dashboard) },
      { path: RouterPaths.Dashboard, element: <Dashboard /> },
      {
        path: RouterPaths.FeeGroups,
        element: <FeeGroups />,
        loader: () => permissionsGuard(['READ_FEE_GROUPS']),
      },
      {
        path: RouterPaths.FeeGroupDetails,
        element: <FeeGroupDetails />,
        loader: () => permissionsGuard(['READ_FEES']),
        children: [
          {
            index: true,
            loader: ({ params }) =>
              redirect(`${RouterPaths.FeeGroups}/${params.fee_group_id}/fee-group-details/fees`),
          },
          {
            path: RouterPaths.Fees,
            element: <Fees />,
            loader: () => permissionsGuard(['READ_FEES']),
          },
          {
            path: RouterPaths.FeeLevels,
            element: <FeeLevels />,
            loader: () => permissionsGuard(['READ_FEE_LEVELS']),
          },
        ],
      },
      {
        path: RouterPaths.ProviderFees,
        element: <Provider />,
        loader: () => permissionsGuard(['READ_EXCHANGE_PROVIDERS']),
      },
      {
        path: RouterPaths.ProvidersFee,
        element: <ProviderFees />,
        loader: () => permissionsGuard(['READ_EXCHANGE_PROVIDER_FEES']),
      },
      {
        path: RouterPaths.BlockchainFeeMarkups,
        element: <BlockchainFeeMarkups />,
        loader: () => permissionsGuard(['READ_BLOCKCHAIN_FEE_MARKUPS']),
      },
      {
        path: RouterPaths.ExchangeFeeGroups,
        element: <ExchangeFeeGroups />,
        loader: () => permissionsGuard(['READ_RATE_MARKUP_GROUPS']),
      },
      {
        path: RouterPaths.MerchantCoinConfig,
        element: <MerchantСoinСonfig />,
        loader: () => permissionsGuard(['READ_MERCHANT_COIN_CONFIGS']),
      },
      {
        path: RouterPaths.ExchangeFees,
        element: <ExchangeFees />,
        loader: () => permissionsGuard(['READ_RATE_MARKUPS']),
      },
      {
        path: RouterPaths.Profiles,
        element: <Profiles />,
        loader: () => permissionsGuard(['READ_PROFILES']),
      },
      {
        path: RouterPaths.Transactions,
        element: <Transactions componentType="default" />,
        loader: () => permissionsGuard(['READ_TRANSACTIONS']),
      },
      {
        path: RouterPaths.Transaction,
        element: <Transaction />,
        loader: () => permissionsGuard(['READ_FEE_GROUPS']),
      },
      {
        path: RouterPaths.ProfileTransaction,
        element: <Transaction />,
        loader: () => permissionsGuard(['READ_FEE_GROUPS']),
      },
      {
        path: RouterPaths.ProfileMerchantPaymentDetailsTransaction,
        element: <Transaction />,
        loader: () => permissionsGuard(['READ_FEE_GROUPS']),
      },
      {
        path: RouterPaths.Invoices,
        element: <Invoices />,
        loader: () => permissionsGuard(['READ_WALLET_DEPOSIT_INVOICES']),
      },
      {
        path: RouterPaths.Dictionaries,
        element: <Dictionaries />,
        children: [
          {
            index: true,
            loader: () => redirect(RouterPaths.Countries),
          },
          {
            path: RouterPaths.Countries,
            element: <Countries />,
            loader: () => permissionsGuard(['READ_COUNTRIES']),
          },
          {
            path: RouterPaths.CompanyTypes,
            element: <CompanyTypes />,
            loader: () => permissionsGuard(['READ_COMPANY_TYPES']),
          },
        ],
      },
      {
        path: RouterPaths.UserAndRoles,
        element: <UsersAndRoles />,
        children: [
          {
            index: true,
            loader: () => redirect(RouterPaths.Users),
          },
          {
            path: RouterPaths.Users,
            element: <Users />,
            loader: () => permissionsGuard(['READ_ROLES']),
          },
          {
            path: RouterPaths.Roles,
            element: <Roles />,
            loader: () => permissionsGuard(['READ_ROLES']),
          },
          {
            path: RouterPaths.RoleDetails,
            element: <RoleDetails />,
            loader: () => permissionsGuard(['WRITE_ROLES']),
          },
        ],
      },
      {
        path: RouterPaths.ExchangeRates,
        element: <ExchangeRates />,
        loader: () => permissionsGuard(['READ_RATES']),
      },
      {
        path: RouterPaths.BlockchainFee,
        element: <BlockchainFee />,
        loader: () => permissionsGuard(['READ_TRANSIT_ADDRESSES']),
      },
      {
        path: RouterPaths.Withdrawals,
        element: <Transactions componentType="forWithdrawals" />,
        loader: () => permissionsGuard(['READ_TRANSACTIONS']),
      },
      {
        path: RouterPaths.TransitAddresses,
        element: <TransitAddresses />,
        loader: () => permissionsGuard(['READ_TRANSIT_ADDRESSES']),
      },
      {
        path: RouterPaths.TransitWithdrawals,
        element: <TransitWithdrawals />,
        loader: () => permissionsGuard(['READ_TRANSIT_ADDRESSES']),
      },
      {
        path: RouterPaths.ABFPaymentApps,
        element: <ABFPaymentApps />,
        loader: () => permissionsGuard(['READ_APPLICATIONS']),
      },
      {
        path: RouterPaths.ApplicationTokens,
        element: <ApplicationTokens />,
        loader: () => permissionsGuard(['READ_APPLICATION_TOKENS']),
      },
      {
        path: RouterPaths.Reports,
        element: <Reports />,
        loader: () => permissionsGuard(['READ_REPORTS']),
      },
      {
        path: RouterPaths.Profile,
        element: <Profile />,
        loader: () => permissionsGuard(['READ_PROFILES']),
        children: [
          {
            index: true,
            loader: ({ params }) =>
              redirect(`${RouterPaths.Profiles}/${params.profileUid}/details`),
          },
          {
            path: RouterPaths.ProfileDetails,
            element: <Details />,
            loader: () => permissionsGuard(['READ_FEE_GROUPS']),
          },
          {
            path: RouterPaths.ProfileDocuments,
            element: <Documents />,
            loader: () => permissionsGuard(['READ_FEE_GROUPS']),
          },
          {
            path: RouterPaths.ProfileBankAccounts,
            element: <BankAccounts />,
            loader: () => permissionsGuard(['READ_BANK_ACCOUNTS']),
          },
          {
            path: RouterPaths.ProfileWallets,
            element: <Wallets />,
            loader: () => permissionsGuard(['READ_FEE_GROUPS']),
          },
          {
            path: RouterPaths.ProfileTransactions,
            element: <Transactions componentType="forProfile" />,
            loader: () => permissionsGuard(['READ_TRANSACTIONS']),
          },
          {
            path: RouterPaths.ProfileMerchantConfig,
            element: <MerchantConfig />,
            loader: () => permissionsGuard(['READ_MERCHANT_COIN_CONFIGS']),
          },
          // {
          //   path: RouterPaths.ProfileMerchantCoinConfig,
          //   element: <MerchantCoinConfig />,
          //   loader: () => permissionsGuard(['READ_MERCHANT_COIN_CONFIGS']),
          // },
          {
            path: RouterPaths.ProfileMerchantPayments,
            element: <MerchantPayments />,
            loader: () => permissionsGuard(['READ_MERCHANTS']),
          },
          {
            path: RouterPaths.ProfileMerchantPaymentDetails,
            element: <MerchantPaymentDetails />,
            loader: () => permissionsGuard(['READ_MERCHANTS']),
          },
          {
            path: RouterPaths.ProfileSandboxUsers,
            element: <SandboxUsers />,
          },
          { path: RouterPaths.ProfileSumsubInfo, element: <SumsubInfo /> },
        ],
      },
      {
        path: RouterPaths.Providers,
        element: <Providers />,
        loader: () => permissionsGuard(['READ_EXCHANGE_PROVIDERS']),
        children: [
          // {
          //   index: true,
          //   loader: () => redirect(`${RouterPaths.Providers}`),
          // },
          { path: RouterPaths.ProvidersWallets, element: <ProviderWallet /> },
          { path: RouterPaths.Ledgers, element: <Ledgers /> },
        ],
      },
      {
        path: RouterPaths.NotificationTemplates,
        element: <NotificationTemplates />,
        loader: () => permissionsGuard(['READ_NOTIFICATION_TEMPLATES']),
      },
      {
        path: RouterPaths.NotificationTemplate,
        element: <NotificationTemplate />,
        loader: () => permissionsGuard(['READ_NOTIFICATION_TEMPLATES']),
      },
    ],
  },
];
